// 订阅号小舍AI热度号工作台（v6.10）——独立副业号入口
// 功能：①今日热点选题（读 Content/HotTopics/今日热点-{date}.md，可选中）
//      ②基于选中热点【一键生成】热度文章（DeepSeek + 创始人口吻，保存到 Content/订阅号小舍AI/）
//      ③文章列表（点开预览：复制标题 / 复制全文）
//      ④【生成封面图】AI 无字背景 + Canvas 叠加清晰中文标题（契合内容、带文字、不乱码）
// 原则：不走主链路，手动发布；口吻=热度号口吻卡；文章不植入 XEAOS
import { useEffect, useState, useCallback } from "react";
import {
  Flame,
  Newspaper,
  FileText,
  Copy,
  CheckCircle2,
  TrendingUp,
  AlertCircle,
  Wand2,
  Loader2,
  ImageIcon,
  Download,
  Type,
} from "lucide-react";
import { ImageGenerationService } from "../../services/image";

interface HotTopic {
  title: string;
  angle: string;
}

interface SubArticle {
  path: string;
  name: string;
  mtime: string;
}

// 作者可用素材（仅供"点睛"，禁止铺陈履历）
const FOUNDER_BG = `作者真实背景（只是可调用素材，不是写作主体）：做过制造业、酒店智能化、二手车金融，2025 年底起全职做 AI 创业、做企业 AI 系统。写作时如果某个观点需要真实感支撑，可以自然带出一句相关经历（比如"我之前做过制造业""我现在做 AI 创业，见过很多……"）；但【禁止】像简历一样罗列经历（不写"从华为到电池厂再到白酒"这种报菜名式列举），经历全篇最多出现 1-2 处，每处只一句。`;

// 热度号口吻规范（与《订阅号小舍AI热度号作战卡》一致）
// 核心原则：口吻为主，经历点睛——文章的灵魂是作者的说话方式，不是经历清单
const TONE_RULES = `【最高原则：口吻为主，经历点睛】读者爱看的是"这人的说话方式"，不是"这人的经历"。全文用作者的口吻写，经历只在论证时带一句做点睛，绝不铺陈。
写作公式：标题钩子（含热点词+冲突/数字/悬念，30 字内）→ 开头 1-2 句接住热点并给态度 → 正文 3-5 段（每段一个小观点+生活化例子/一个自然带出的真实细节）→ 结尾观点升华+反问收束。全文 600-1200 字，短段（3-4 行一段），多空行。
口吻要点（这是灵魂，必须模仿到位）：
- 判断开场：第一句就亮态度，不铺垫不绕弯（"说白了……""我的看法是……"）
- 大白话：口语化用词（"对吧""说真的""你以为呢"），像跟朋友聊天，不像写文章
- 爱打比方：抽象概念用生活化的比喻讲透（"AI 是文科生，会一本正经胡说八道""AI 是放大器，你学的东西是信号源"）
- 观点鲜明敢下结论：不骑墙，敢于说"这事我不看好""这就是割韭菜"
- 短句节奏：一句话一段，断句短，读起来有劲儿
- 真实感（点睛）：需要佐证时自然带一句"我之前做……的时候""我见过太多……"，语气像顺口提到，不是自我介绍
- 结尾反问：把话题抛回读者（"你觉得呢？"）
硬性红线：不追政治/灾难热点；不造谣不传谣（引用注明来源）；不做"震惊体"标题党；全原创；【不得提及 XEAOS、小舍科技、官网、联系方式等品牌信息】——本号是独立赚钱号，文章只谈热点观点。
【爆款内容价值体系（v6.18：真实落地为底线，情绪为底座，按内容类型组合 3-4 个维度）】开头用目标读者口吻描述真实处境（"你是不是也……"）3 秒共鸣"说的就是我"；给 1-3 个能带走的点（功利/认知价值）；敢表达与主流不同的真实观点（认知反转版冲突钩子，禁焦虑对立）；埋 1 个金句记忆点；有"只有你有"的稀缺视角（独家经历/内部视角）；有转发理由（社交货币）；场景案例必须真实禁编造。`;

// 解析文章标题：frontmatter topic 优先，其次正文第一个 # 标题
const parseTitle = (content: string): string => {
  const fm = content.match(/^---\n([\s\S]*?)\n---/);
  if (fm) {
    const m = fm[1].match(/^topic:\s*(.+)$/m);
    if (m) return m[1].trim();
  }
  const h = content.match(/^#\s+(.+)$/m);
  if (h) return h[1].trim();
  return content.slice(0, 30);
};

// 去掉 frontmatter 取正文前 120 字作为封面摘要
const makeSummary = (content: string): string => {
  const body = content.replace(/^---[\s\S]*?---\n*/, "").trim();
  return body.replace(/\s+/g, " ").slice(0, 120);
};

// Canvas 合成封面：AI 背景图 + 清晰中文标题（文字用系统字体渲染，不乱码）
// v6.13：订阅号为公众号 → 横版封面 1792x1024
// v6.14：背景图加载失败时兜底为渐变背景+标题（封面不因 CDN 抖动而全失败）
const composeCover = async (bgUrl: string, title: string): Promise<string> => {
  const W = 1792;
  const H = 1024;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 不可用");
  let bgLoaded = false;
  // 经服务器 /img-proxy 加载（绕过外域图片 CORS，v6.1.10 既有代理）
  try {
    const blobResp = await fetch(`/img-proxy?url=${encodeURIComponent(bgUrl)}`).catch(() => null);
    if (blobResp && blobResp.ok) {
      const blob = await blobResp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("背景图解析失败"));
        img.src = objectUrl;
      });
      const scale = Math.max(W / img.width, H / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
      URL.revokeObjectURL(objectUrl);
      bgLoaded = true;
    }
  } catch {
    // 背景图失败：走兜底
  }
  if (!bgLoaded) {
    // 兜底：深色渐变背景（保证封面仍可用，标题文字正常叠加）
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "#1a1a2e");
    g.addColorStop(0.5, "#16213e");
    g.addColorStop(1, "#0f3460");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    ctx.beginPath();
    ctx.arc(W * 0.85, H * 0.2, 260, 0, Math.PI * 2);
    ctx.fill();
  }
  // 底部渐变遮罩（保证文字可读）
  const grad = ctx.createLinearGradient(0, H * 0.45, 0, H);
  grad.addColorStop(0, "rgba(0,0,0,0)");
  grad.addColorStop(1, "rgba(0,0,0,0.82)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, H * 0.4, W, H * 0.6);
  // 顶部左侧小字（号标签）
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "600 32px system-ui, 'PingFang SC', 'Microsoft YaHei', sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("小舍AI · 热点观察", 70, 90);
  // 标题文字（自动换行，大号，白字+描边，横版底部区域）
  const maxWidth = W - 140;
  const fontSize = 104;
  const lineHeight = 128;
  ctx.font = `700 ${fontSize}px system-ui, 'PingFang SC', 'Microsoft YaHei', sans-serif`;
  const chars = title.split("");
  const lines: string[] = [];
  let cur = "";
  for (const ch of chars) {
    if (ctx.measureText(cur + ch).width > maxWidth && cur) {
      lines.push(cur);
      cur = ch;
    } else {
      cur += ch;
    }
  }
  if (cur) lines.push(cur);
  const showLines = lines.slice(0, 3); // 横版最多 3 行
  const startY = H - 300;
  ctx.textAlign = "left";
  showLines.forEach((line, i) => {
    const y = startY + i * lineHeight;
    ctx.lineWidth = 10;
    ctx.strokeStyle = "rgba(0,0,0,0.85)";
    ctx.strokeText(line, 70, y);
    ctx.fillStyle = "#ffffff";
    ctx.fillText(line, 70, y);
  });
  // 底部右侧日期
  const d = new Date();
  ctx.font = "400 30px system-ui, 'PingFang SC', 'Microsoft YaHei', sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.textAlign = "right";
  ctx.fillText(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`, W - 70, H - 70);
  return canvas.toDataURL("image/png");
};

export default function SubscriptionHub() {
  const [hotTopics, setHotTopics] = useState<HotTopic[]>([]);
  const [hotDate, setHotDate] = useState("");
  const [articles, setArticles] = useState<SubArticle[]>([]);
  const [articleContent, setArticleContent] = useState<{ path: string; content: string } | null>(null);
  const [copied, setCopied] = useState<"title" | "full" | null>(null);
  const [loading, setLoading] = useState(true);
  // 生成状态
  const [selectedHot, setSelectedHot] = useState<number>(0);
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // 封面状态
  const [coverState, setCoverState] = useState<{ generating: boolean; url: string | null; error: string | null }>({
    generating: false,
    url: null,
    error: null,
  });

  const today = (() => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  })();

  const load = useCallback(async () => {
    setLoading(true);
    setHotDate(today);
    try {
      const hotResp = await fetch(
        `/vault-api/read?path=${encodeURIComponent(
          `10_Operations（运营层）/Content/HotTopics/今日热点-${today}.md`
        )}`
      ).catch(() => null);
      if (hotResp?.ok) {
        const r = await hotResp.json();
        if (r.success && r.content) {
          const list: HotTopic[] = [];
          for (const line of r.content.split("\n")) {
            const m = line.match(/^\s*-\s+([^|]+?)\s*\|\s*(.+?)\s*$/);
            if (m) list.push({ title: m[1].trim(), angle: m[2].trim() });
          }
          setHotTopics(list);
        }
      }
    } catch {
      // 忽略
    }
    try {
      const listResp = await fetch(
        `/vault-api/list?dir=${encodeURIComponent(
          `10_Operations（运营层）/Content/订阅号小舍AI`
        )}&ext=.md`
      ).catch(() => null);
      if (listResp?.ok) {
        const r = await listResp.json();
        if (r.success && r.files) {
          setArticles(
            r.files
              .filter((f: { isDirectory?: boolean }) => !f.isDirectory)
              .sort((a: { mtime?: string }, b: { mtime?: string }) =>
                (b.mtime || "").localeCompare(a.mtime || "")
              )
          );
        }
      }
    } catch {
      // 忽略
    }
    setLoading(false);
  }, [today]);

  useEffect(() => {
    void load();
  }, [load]);

  const openArticle = async (path: string) => {
    try {
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(path)}`).catch(
        () => null
      );
      if (resp?.ok) {
        const r = await resp.json();
        if (r.success) {
          setArticleContent({ path, content: r.content });
          setCopied(null);
          setCoverState({ generating: false, url: null, error: null });
        }
      }
    } catch {
      // 忽略
    }
  };

  const copyText = async (text: string, kind: "title" | "full") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // 忽略
    }
  };

  const stripFm = (content: string) => {
    const m = content.match(/^---[\s\S]*?---\n*([\s\S]*)$/);
    return (m ? m[1] : content).trim();
  };

  // ============ 生成热点文章 ============
  // P12 bundle 密钥治理：DeepSeek 密钥由后端 /deepseek-api 代理统一代填，
  // 前端不再读取 VITE_DEEPSEEK_API_KEY，也不再要求前端持有 key。

  const generateArticle = async () => {
    const hot = hotTopics[selectedHot];
    if (!hot || generating) return;
    setGenerating(true);
    setGenMsg(null);
    try {
      const sysPrompt = `你是个人公众号「小舍AI」的写作助手，为作者撰写热度文章。\n${FOUNDER_BG}\n${TONE_RULES}\n严格按作者口吻第一人称写作，不出现任何品牌信息。`;
      const userPrompt = `今日热点选题：${hot.title}\n适配角度：${hot.angle}\n\n请按写作公式撰写一篇 600-1200 字的公众号热度文章（含标题，标题后空一行再写正文）。`;
      const resp = await fetch("/deepseek-api", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "deepseek-chat",
          messages: [
            { role: "system", content: sysPrompt },
            { role: "user", content: userPrompt },
          ],
          temperature: 0.75,
          max_tokens: 3000,
          stream: false,
        }),
      });
      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(`生成失败 (${resp.status}): ${errText.slice(0, 150)}`);
      }
      const data = await resp.json();
      // 兼容两种响应格式：vite 直达 OpenAI 格式 {choices} / 生产 server.py 代理 {success,content}
      const content = (data?.choices?.[0]?.message?.content || data?.content || "") as string;
      if (!content) throw new Error("生成返回空内容");
      const cleaned = content.trim();

      const safeTopic = hot.title.replace(/[^\w\u4e00-\u9fa5]/g, "").slice(0, 14);
      const path = `10_Operations（运营层）/Content/订阅号小舍AI/文章-${today}-${safeTopic}.md`;
      const fm = `---\ntype: subscription_article\npurpose: 订阅号热度文章（手动发布）\ndate: ${today}\nstatus: 待发布\ntopic: ${hot.title}\n---\n\n`;
      const writeResp = await fetch(`/vault-api/write?path=${encodeURIComponent(path)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: fm + cleaned }),
      });
      if (!writeResp.ok) throw new Error("保存到 Vault 失败");
      setGenMsg({ ok: true, text: `✅ 已生成并保存：${safeTopic}` });
      await openArticle(path);
      await load();
    } catch (e: any) {
      setGenMsg({ ok: false, text: e?.message || "生成失败，请重试" });
    } finally {
      setGenerating(false);
    }
  };

  // ============ 生成封面图（AI 背景 + Canvas 叠加清晰中文标题） ============
  const generateCover = async () => {
    if (!articleContent || coverState.generating) return;
    setCoverState({ generating: true, url: null, error: null });
    try {
      const title = parseTitle(articleContent.content);
      const summary = makeSummary(articleContent.content);
      const svc = new ImageGenerationService();
      const result = await svc.generateWeChatCover(
        title,
        summary,
        "公众号文章封面，画面必须体现文章主题相关的具体场景或对象，现代专业风格，高质感，禁止抽象渐变背景"
      );
      const coverUrl = result.images?.[0]?.url;
      if (!result.success || !coverUrl) {
        throw new Error(result.error || "封面生成失败");
      }
      const composed = await composeCover(coverUrl, title);
      setCoverState({ generating: false, url: composed, error: null });
    } catch (e: any) {
      setCoverState({ generating: false, url: null, error: e?.message || "封面生成失败" });
    }
  };

  const downloadCover = () => {
    if (!coverState.url) return;
    const a = document.createElement("a");
    a.href = coverState.url;
    a.download = `封面-${parseTitle(articleContent?.content || "")}.png`;
    a.click();
  };

  return (
    <div className="space-y-3 p-4">
      {/* 头部 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold text-white tracking-tight">
            订阅号「小舍AI」热度号
          </span>
          <span className="text-[9px] font-mono text-gray-500">副业现金流 · 独立于主链路</span>
        </div>
        <button
          onClick={() => void load()}
          className="px-2.5 py-1 rounded-md text-[10px] font-mono text-gray-400 hover:bg-white/[0.04] border border-white/[0.08]"
        >
          刷新
        </button>
      </div>

      {loading ? (
        <div className="text-[11px] text-gray-500 py-6 text-center">加载中…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* 左列：今日热点 + 生成 + 文章列表 */}
          <div className="space-y-3">
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Flame size={12} className="text-orange-400" />
                <span className="text-[11px] font-semibold text-orange-300">
                  今日热点选题（{hotDate}）
                </span>
                <span className="text-[9px] font-mono text-gray-600 ml-auto">点击选中</span>
              </div>
              {hotTopics.length === 0 ? (
                <div className="text-[10px] text-gray-500 py-2">
                  今日热点库未生成——06:00 自动化会先于内容工厂写入，稍后刷新即可
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
                  {hotTopics.map((t, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedHot(i)}
                      className={`w-full text-left rounded-md border px-2 py-1.5 transition-colors ${
                        selectedHot === i
                          ? "border-orange-500/40 bg-orange-500/10"
                          : "border-white/[0.05] bg-white/[0.02] hover:bg-white/[0.05]"
                      }`}
                    >
                      <div className="text-[10px] text-gray-200">{t.title}</div>
                      <div className="text-[9px] text-gray-500 mt-0.5 truncate">🎯 {t.angle}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
              <button
                onClick={() => void generateArticle()}
                disabled={generating || hotTopics.length === 0}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-semibold text-black bg-gradient-to-r from-orange-400 to-amber-300 hover:from-orange-300 hover:to-amber-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {generating ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                {generating ? "生成中（约 20 秒）…" : "生成热点文章"}
              </button>
              {genMsg && (
                <div className={`mt-2 text-[10px] ${genMsg.ok ? "text-emerald-400" : "text-red-400"}`}>
                  {genMsg.text}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <FileText size={12} className="text-sky-400" />
                <span className="text-[11px] font-semibold text-sky-300">订阅号文章</span>
                <span className="text-[9px] font-mono text-gray-600 ml-auto">{articles.length} 篇</span>
              </div>
              {articles.length === 0 ? (
                <div className="text-[10px] text-gray-500 py-2">
                  暂无文章——选一个热点点「生成热点文章」即可产出
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1">
                  {articles.map((a) => (
                    <button
                      key={a.path}
                      onClick={() => void openArticle(a.path)}
                      className={`w-full text-left rounded-md border px-2 py-1.5 transition-colors ${
                        articleContent?.path === a.path
                          ? "border-sky-500/40 bg-sky-500/10"
                          : "border-white/[0.05] bg-white/[0.02] hover:bg-white/[0.05]"
                      }`}
                    >
                      <div className="text-[10px] text-gray-200 truncate">
                        {a.name.replace(".md", "")}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 中列：文章预览（复制标题/复制全文） */}
          <div className="space-y-3">
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 min-h-[320px]">
              <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                <Newspaper size={12} className="text-emerald-400" />
                <span className="text-[11px] font-semibold text-emerald-300">文章预览</span>
                {articleContent && (
                  <div className="ml-auto flex items-center gap-1.5">
                    <button
                      onClick={() => void copyText(parseTitle(articleContent.content), "title")}
                      className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono text-sky-400 border border-sky-500/30 hover:bg-sky-500/10"
                    >
                      {copied === "title" ? <CheckCircle2 size={10} /> : <Type size={10} />}
                      {copied === "title" ? "标题已复制" : "复制标题"}
                    </button>
                    <button
                      onClick={() => void copyText(stripFm(articleContent.content), "full")}
                      className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/10"
                    >
                      {copied === "full" ? <CheckCircle2 size={10} /> : <Copy size={10} />}
                      {copied === "full" ? "已复制" : "复制全文"}
                    </button>
                  </div>
                )}
              </div>
              {!articleContent ? (
                <div className="text-[10px] text-gray-500 py-4 text-center">
                  点选文章 → 这里预览全文 → 复制标题/复制全文 → 粘贴发布
                </div>
              ) : (
                <>
                  <div className="text-[11px] font-semibold text-white mb-1">
                    {parseTitle(articleContent.content)}
                  </div>
                  <div className="max-h-[420px] overflow-y-auto pr-1 border-t border-white/[0.05] pt-2">
                    <pre className="whitespace-pre-wrap text-[10px] leading-relaxed text-gray-300 font-sans">
                      {stripFm(articleContent.content)}
                    </pre>
                  </div>
                </>
              )}
            </div>

            {/* 作战卡要点 */}
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <TrendingUp size={12} className="text-purple-400" />
                <span className="text-[11px] font-semibold text-purple-300">作战卡要点</span>
              </div>
              <div className="space-y-1 text-[10px] text-gray-400 leading-relaxed">
                <div>📐 <b className="text-gray-300">公式</b>：标题钩子 → 接热点 → 3-5 段观点 → 反问收尾，600-1200 字</div>
                <div>🗣 <b className="text-gray-300">最高原则</b>：口吻为主，经历点睛（经历全篇 ≤2 处）</div>
                <div>🚫 <b className="text-gray-300">红线</b>：不追政治/灾难、不造谣、不标题党、不植入 XEAOS</div>
                <div>💰 <b className="text-gray-300">收益</b>：500 粉开流量主 → 月入千元需稳定小爆款</div>
                <div>📌 <b className="text-gray-300">发布</b>：复制标题+全文 → mp.weixin.qq.com → 图文 → 群发（手动）</div>
              </div>
            </div>
          </div>

          {/* 右列：封面生成 */}
          <div className="space-y-3">
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <ImageIcon size={12} className="text-fuchsia-400" />
                <span className="text-[11px] font-semibold text-fuchsia-300">封面图生成</span>
                <span className="text-[9px] font-mono text-gray-600 ml-auto">横版 1792×1024</span>
              </div>
              {!articleContent ? (
                <div className="text-[10px] text-gray-500 py-4 text-center">
                  先选中一篇文章，再生成封面（AI 背景 + 清晰中文标题）
                </div>
              ) : (
                <>
                  <button
                    onClick={() => void generateCover()}
                    disabled={coverState.generating}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-semibold text-black bg-gradient-to-r from-fuchsia-400 to-purple-300 hover:from-fuchsia-300 hover:to-purple-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    {coverState.generating ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <ImageIcon size={12} />
                    )}
                    {coverState.generating
                      ? "封面生成中（排队 1-20 分钟，请勿关闭页面）…"
                      : "生成封面图"}
                  </button>
                  {coverState.generating && (
                    <div className="mt-2 text-[9px] text-gray-500">
                      AI 正在按文章内容生成背景图（约 1-5 分钟），完成后自动叠加标题文字
                    </div>
                  )}
                  {coverState.error && (
                    <div className="mt-2 text-[10px] text-red-400">{coverState.error}</div>
                  )}
                  {coverState.url && (
                    <div className="mt-2">
                      <img
                        src={coverState.url}
                        alt="封面预览"
                        className="w-full rounded-lg border border-white/[0.1]"
                      />
                      <button
                        onClick={downloadCover}
                        className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono text-fuchsia-300 border border-fuchsia-500/30 hover:bg-fuchsia-500/10"
                      >
                        <Download size={11} /> 下载封面 PNG
                      </button>
                      <div className="mt-1 text-[9px] text-gray-600">
                        已自动叠加标题文字（系统字体渲染，保证清晰不乱码）
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* 提示 */}
            <div className="flex items-start gap-1.5 rounded-lg border border-amber-500/20 bg-amber-500/[0.04] px-2 py-1.5">
              <AlertCircle size={11} className="text-amber-400 mt-0.5 shrink-0" />
              <div className="text-[9px] text-amber-300/80 leading-relaxed">
                封面图 = AI 背景（契合文章内容）+ 清晰中文标题。不走主链路、不自动发布；发布时先复制标题再复制全文。
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
